import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { humanizeColumnName, formatScore } from '../../lib/formatters';
import { getOrderedDemographicValues } from '../../lib/analysisUtils';
import { SEMANTIC_HEX } from '../../lib/colors';

function lerpColor(colorA, colorB, t) {
  const a = parseInt(colorA.slice(1), 16);
  const b = parseInt(colorB.slice(1), 16);
  const rA = (a >> 16) & 0xff, gA = (a >> 8) & 0xff, bA = a & 0xff;
  const rB = (b >> 16) & 0xff, gB = (b >> 8) & 0xff, bB = b & 0xff;
  const r = Math.round(rA + (rB - rA) * t);
  const g = Math.round(gA + (gB - gA) * t);
  const bl = Math.round(bA + (bB - bA) * t);
  return `rgb(${r},${g},${bl})`;
}

const VIEW_MODES = [
  { value: 'distribution', label: 'Distribution %' },
  { value: 'delta',        label: 'Δ vs Average' },
  { value: 'indexed',      label: 'Indexed (100 = avg)' },
];

const SORT_MODES = [
  { value: 'natural',    label: 'Natural order' },
  { value: 'support',    label: 'Highest support' },
  { value: 'resistance', label: 'Biggest resistance' },
  { value: 'population', label: 'Largest population' },
  { value: 'deviation',  label: 'Biggest deviation' },
];

export function DemographicHeatmap({ allBreakdowns, orderedResponseScale, scaleColors, scaleColorMap, globalPcts }) {
  const demographics = useMemo(() => {
    if (!allBreakdowns) return [];
    return Object.keys(allBreakdowns).filter(k => allBreakdowns[k]?.breakdown && Object.keys(allBreakdowns[k].breakdown).length >= 2);
  }, [allBreakdowns]);

  const [selectedDemographic, setSelectedDemographic] = useState('');
  const [viewMode, setViewMode] = useState('distribution');
  const [sortMode, setSortMode] = useState('natural');

  useEffect(() => {
    if (demographics.length > 0 && !selectedDemographic) {
      setSelectedDemographic(demographics[0]);
    }
  }, [demographics, selectedDemographic]);

  // Sentiment classification: first 40% of options = negative, last 40% = positive
  const sentimentMap = useMemo(() => {
    const n = orderedResponseScale.length;
    const map = {};
    orderedResponseScale.forEach((opt, i) => {
      const ratio = n > 1 ? i / (n - 1) : 0.5;
      if (ratio < 0.4) map[opt] = 'negative';
      else if (ratio > 0.6) map[opt] = 'positive';
      else map[opt] = 'neutral';
    });
    return map;
  }, [orderedResponseScale]);

  const heatmapData = useMemo(() => {
    if (!allBreakdowns || orderedResponseScale.length === 0 || !selectedDemographic) return [];
    const demosToShow = selectedDemographic === '__all__' ? demographics : [selectedDemographic];
    const rows = [];
    for (const demoName of demosToShow) {
      const demoData = allBreakdowns[demoName];
      if (!demoData?.breakdown) continue;
      const groups = getOrderedDemographicValues(Object.keys(demoData.breakdown), demoName);
      for (const group of groups) {
        const data = demoData.breakdown[group];
        if (!data?.percentages) continue;
        const values = orderedResponseScale.map(resp => data.percentages[resp] || 0);
        const count = Object.values(data.distribution || {}).reduce((s, v) => s + v, 0);

        // Pre-compute support / resistance / mean for sorting
        let support = 0, resistance = 0;
        orderedResponseScale.forEach((opt, i) => {
          const v = values[i];
          if (sentimentMap[opt] === 'positive') support += v;
          else if (sentimentMap[opt] === 'negative') resistance += v;
        });
        // Approx mean using 1..N scale
        const mean = orderedResponseScale.reduce((s, _, i) => s + (i + 1) * values[i], 0) / 100;
        // Max abs deviation from overall
        const maxDev = Math.max(...orderedResponseScale.map((opt, i) => Math.abs(values[i] - (globalPcts?.[opt] || 0))));

        rows.push({ group, demo: demoName, values, count, support, resistance, mean, maxDev });
      }
    }
    return rows;
  }, [allBreakdowns, orderedResponseScale, selectedDemographic, demographics, sentimentMap, globalPcts]);

  const sortedData = useMemo(() => {
    if (sortMode === 'natural') return heatmapData;
    const arr = [...heatmapData];
    if (sortMode === 'support') arr.sort((a, b) => b.support - a.support);
    else if (sortMode === 'resistance') arr.sort((a, b) => b.resistance - a.resistance);
    else if (sortMode === 'population') arr.sort((a, b) => b.count - a.count);
    else if (sortMode === 'deviation') arr.sort((a, b) => b.maxDev - a.maxDev);
    return arr;
  }, [heatmapData, sortMode]);

  const maxPct = useMemo(() => {
    if (heatmapData.length === 0) return 1;
    let max = 0;
    for (const row of heatmapData) for (const v of row.values) if (v > max) max = v;
    return max || 1;
  }, [heatmapData]);

  // Find biggest +/- gap cells (auto-highlight)
  const { biggestPosCell, biggestNegCell } = useMemo(() => {
    let posCell = null, negCell = null;
    for (const row of sortedData) {
      orderedResponseScale.forEach((opt, i) => {
        const diff = row.values[i] - (globalPcts?.[opt] || 0);
        if (!posCell || diff > posCell.diff) posCell = { row: row.group, opt, diff };
        if (!negCell || diff < negCell.diff) negCell = { row: row.group, opt, diff };
      });
    }
    return { biggestPosCell: posCell, biggestNegCell: negCell };
  }, [sortedData, orderedResponseScale, globalPcts]);

  if (!allBreakdowns || demographics.length === 0 || orderedResponseScale.length === 0) return null;

  return (
    <Card className="border border-border" data-testid="demographic-heatmap">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base">Response by Demographic Heatmap</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Drill into segment × response patterns. Hover any cell for details.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="heatmap-view-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{VIEW_MODES.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sortMode} onValueChange={setSortMode}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="heatmap-sort-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{SORT_MODES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={selectedDemographic} onValueChange={setSelectedDemographic}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="heatmap-demographic-selector">
                <SelectValue placeholder="Select demographic" />
              </SelectTrigger>
              <SelectContent>
                {demographics.map(d => <SelectItem key={d} value={d}>{humanizeColumnName(d)}</SelectItem>)}
                <SelectItem value="__all__">All Demographics</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {sortedData.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No breakdown data available</p>
        ) : (
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto rounded-lg border border-slate-100 dark:border-slate-700" data-testid="heatmap-scroll-container">
            <table className="w-full border-collapse text-xs" data-testid="heatmap-table">
              <thead className="sticky top-0 z-20 bg-white dark:bg-slate-900">
                <tr>
                  <th className="text-left py-2.5 px-3 font-semibold text-slate-500 border-b border-slate-200 sticky left-0 bg-white dark:bg-slate-900 z-30 min-w-[160px]">
                    Group
                  </th>
                  {orderedResponseScale.map((resp) => (
                    <th key={resp} className="text-center py-2.5 px-2 font-semibold border-b border-slate-200 min-w-[80px]"
                      style={{ color: scaleColorMap?.[resp] || scaleColors[orderedResponseScale.indexOf(resp)] || '#94A3B8' }}
                    >
                      <span className="block leading-tight truncate max-w-[88px]" title={resp}>{resp}</span>
                    </th>
                  ))}
                  <th className="text-center py-2.5 px-2 font-semibold text-slate-400 border-b border-slate-200 min-w-[56px]">n</th>
                </tr>
              </thead>
              <tbody>
                {/* Overall average row */}
                {globalPcts && viewMode === 'distribution' && (
                  <tr className="border-b-2 border-slate-200 sticky top-[42px] z-10 bg-slate-50 dark:bg-slate-800">
                    <td className="py-2.5 px-3 font-semibold text-slate-700 dark:text-slate-200 sticky left-0 bg-slate-50 dark:bg-slate-800 z-10">
                      Overall
                    </td>
                    {orderedResponseScale.map((resp) => {
                      const pct = globalPcts[resp] || 0;
                      const intensity = maxPct > 0 ? pct / maxPct : 0;
                      const cellColor = scaleColorMap?.[resp] || scaleColors[orderedResponseScale.indexOf(resp)] || '#3B82F6';
                      const bg = lerpColor('#F8FAFC', cellColor, Math.min(intensity * 0.7, 0.7));
                      const textColor = intensity > 0.5 ? '#1E293B' : '#475569';
                      return (
                        <td key={resp} className="text-center py-2.5 px-2" style={{ backgroundColor: bg }}>
                          <span className="font-semibold" style={{ color: textColor }}>{pct.toFixed(0)}%</span>
                        </td>
                      );
                    })}
                    <td className="text-center py-2.5 px-2 text-slate-400 bg-slate-50 dark:bg-slate-800 font-medium">&mdash;</td>
                  </tr>
                )}
                {sortedData.map((row, rowIdx) => {
                  const prevDemo = rowIdx > 0 ? sortedData[rowIdx - 1].demo : null;
                  const showDemoLabel = row.demo !== prevDemo && selectedDemographic === '__all__';
                  return (
                    <tr key={`${row.demo}-${row.group}`}
                      className={cn(
                        "transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/50",
                        showDemoLabel && rowIdx > 0 && "border-t-2 border-slate-200"
                      )}
                    >
                      <td className="py-2.5 px-3 sticky left-0 bg-white dark:bg-slate-900 z-10">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-slate-700 dark:text-slate-200 truncate" title={row.group}>{row.group}</span>
                          {showDemoLabel && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">{humanizeColumnName(row.demo)}</Badge>
                          )}
                        </div>
                      </td>
                      {row.values.map((pct, idx) => {
                        const respName = orderedResponseScale[idx];
                        const globalVal = globalPcts?.[respName] || 0;
                        const diff = pct - globalVal;
                        const indexed = globalVal > 0 ? (pct / globalVal) * 100 : 0;
                        const cellColor = scaleColorMap?.[respName] || scaleColors[idx] || '#3B82F6';

                        // Cell rendering depends on viewMode
                        let bg, textColor, displayValue;
                        if (viewMode === 'distribution') {
                          const intensity = maxPct > 0 ? pct / maxPct : 0;
                          bg = lerpColor('#F8FAFC', cellColor, Math.min(intensity * 0.7, 0.7));
                          textColor = intensity > 0.5 ? '#1E293B' : '#64748B';
                          displayValue = `${pct.toFixed(0)}%`;
                        } else if (viewMode === 'delta') {
                          // Diverging coral / teal scale
                          const t = Math.min(Math.abs(diff) / 20, 1); // 20pp = max saturation
                          if (diff > 0) bg = lerpColor('#ffffff', SEMANTIC_HEX.positive, t * 0.6);
                          else if (diff < 0) bg = lerpColor('#ffffff', SEMANTIC_HEX.negative, t * 0.6);
                          else bg = '#ffffff';
                          textColor = t > 0.6 ? '#0f172a' : '#475569';
                          displayValue = `${diff > 0 ? '+' : ''}${diff.toFixed(0)}pp`;
                        } else {
                          // Indexed
                          const t = Math.min(Math.abs(indexed - 100) / 50, 1);
                          if (indexed > 100) bg = lerpColor('#ffffff', SEMANTIC_HEX.positive, t * 0.6);
                          else if (indexed < 100) bg = lerpColor('#ffffff', SEMANTIC_HEX.negative, t * 0.6);
                          else bg = '#ffffff';
                          textColor = t > 0.6 ? '#0f172a' : '#475569';
                          displayValue = indexed > 0 ? indexed.toFixed(0) : '—';
                        }

                        // Highlight biggest +/- gaps
                        const isBiggestPos = biggestPosCell && biggestPosCell.row === row.group && biggestPosCell.opt === respName && biggestPosCell.diff > 5;
                        const isBiggestNeg = biggestNegCell && biggestNegCell.row === row.group && biggestNegCell.opt === respName && biggestNegCell.diff < -5;

                        return (
                          <td
                            key={respName}
                            className={cn(
                              "text-center py-2.5 px-2 relative group/cell transition-shadow",
                              isBiggestPos && "ring-2 ring-positive ring-inset",
                              isBiggestNeg && "ring-2 ring-negative ring-inset"
                            )}
                            style={{ backgroundColor: bg }}
                            data-testid={`heatmap-cell-${row.group}-${respName}`}
                          >
                            <span className="font-medium tabular-nums" style={{ color: textColor }}>{displayValue}</span>
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 bg-slate-800 text-white text-[10px] px-2.5 py-1.5 rounded shadow-md opacity-0 group-hover/cell:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-30 leading-tight">
                              <div className="font-semibold">{row.group} · {respName}</div>
                              <div className="text-slate-300 mt-0.5">{pct.toFixed(1)}% — overall {globalVal.toFixed(1)}%</div>
                              <div className={cn("font-mono", diff > 0 ? "text-positive-soft" : diff < 0 ? "text-negative-soft" : "text-slate-300")}>
                                {diff >= 0 ? '+' : ''}{diff.toFixed(1)}pp · index {indexed > 0 ? indexed.toFixed(0) : '—'}
                              </div>
                              <div className="text-slate-400">n = {row.count.toLocaleString()}</div>
                            </div>
                          </td>
                        );
                      })}
                      <td className="text-center py-2.5 px-2 text-slate-400 font-mono">{row.count.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {sortedData.length > 0 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 flex-wrap gap-2">
            <p className="text-[10px] text-slate-400">
              {viewMode === 'distribution' && 'Color = response concentration. Darker = more responses.'}
              {viewMode === 'delta' && 'Color = deviation from overall average. Teal = above, coral = below.'}
              {viewMode === 'indexed' && '100 = matches overall. Above 100 = over-indexed; below 100 = under-indexed.'}
              {' '}<span className="font-medium">Outlined cells</span> mark biggest positive / negative gaps.
            </p>
            {viewMode === 'distribution' && (
              <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                <span>Low</span>
                <div className="flex h-2.5 rounded overflow-hidden">
                  {[0.05, 0.15, 0.3, 0.45, 0.6].map((t, i) => (
                    <div key={i} className="w-5 h-full" style={{ backgroundColor: lerpColor('#F8FAFC', '#3B82F6', t) }} />
                  ))}
                </div>
                <span>High</span>
              </div>
            )}
            {viewMode !== 'distribution' && (
              <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                <span>Below avg</span>
                <div className="flex h-2.5 rounded overflow-hidden">
                  <div className="w-5 h-full" style={{ backgroundColor: lerpColor('#fff', SEMANTIC_HEX.negative, 0.6) }} />
                  <div className="w-5 h-full" style={{ backgroundColor: lerpColor('#fff', SEMANTIC_HEX.negative, 0.3) }} />
                  <div className="w-5 h-full" style={{ backgroundColor: '#fff' }} />
                  <div className="w-5 h-full" style={{ backgroundColor: lerpColor('#fff', SEMANTIC_HEX.positive, 0.3) }} />
                  <div className="w-5 h-full" style={{ backgroundColor: lerpColor('#fff', SEMANTIC_HEX.positive, 0.6) }} />
                </div>
                <span>Above avg</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
